import * as React from "react";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";

const ConfirmContext = React.createContext(null);
/** title, description: ReactNode; confirmText/cancelText: buton metinleri;
 * tone: danger|default; open/onOpenChange: kontrollü görünürlük; onConfirm/onCancel: yanıt.
 * @example <ConfirmDialog open={open} onOpenChange={setOpen} title="Silinsin mi?" tone="danger" onConfirm={sil} />
 */
export function ConfirmDialog({ open, onOpenChange, title, description, confirmText = "Onayla", cancelText = "Vazgeç", tone = "default", onConfirm, onCancel }) {
  const previousFocus = React.useRef(null);
  React.useLayoutEffect(() => {
    if (open) previousFocus.current = document.activeElement;
  }, [open]);
  return <AlertDialog open={open} onOpenChange={(next) => { onOpenChange?.(next); if (!next) onCancel?.(); }}>
    <AlertDialogContent onCloseAutoFocus={(event) => {
      if (previousFocus.current?.isConnected) {
        event.preventDefault();
        previousFocus.current.focus();
      }
    }}><AlertDialogHeader><AlertDialogTitle>{title}</AlertDialogTitle><AlertDialogDescription>{description || "Devam etmek istediğinize emin misiniz?"}</AlertDialogDescription></AlertDialogHeader>
      <AlertDialogFooter><AlertDialogCancel onClick={(event) => { event.preventDefault(); onCancel?.(); onOpenChange?.(false); }}>{cancelText}</AlertDialogCancel><AlertDialogAction variant={tone === "danger" ? "destructive" : "default"} onClick={(event) => { event.preventDefault(); onConfirm?.(); onOpenChange?.(false); }}>{confirmText}</AlertDialogAction></AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>;
}
/** children: uygulama ağacı. Eşzamanlı talepler sırayla açılır; unmount bekleyenleri false ile çözer.
 * @example <ConfirmProvider><App /></ConfirmProvider>
 */
export function ConfirmProvider({ children }) {
  const queue = React.useRef([]);
  const [request, setRequest] = React.useState(null);
  const confirm = React.useCallback((options) => new Promise((resolve) => {
    const entry = { ...options, resolve };
    queue.current.push(entry);
    if (queue.current.length === 1) setRequest(entry);
  }), []);
  const settle = React.useCallback((entry, accepted) => {
    if (!entry || queue.current[0] !== entry) return;
    queue.current.shift();
    entry.resolve(accepted);
    setRequest(queue.current[0] || null);
  }, []);
  React.useEffect(() => () => {
    queue.current.splice(0).forEach((entry) => entry.resolve(false));
  }, []);
  return <ConfirmContext.Provider value={confirm}>{children}<ConfirmDialog {...request} open={!!request} onConfirm={() => settle(request, true)} onCancel={() => settle(request, false)} /></ConfirmContext.Provider>;
}
/** Promise<boolean> döndüren confirm(options) fonksiyonu; provider olmadan anlamlı hata.
 * @example const confirm = useConfirm(); if (!(await confirm({ title: "Silinsin mi?", confirmText: "Sil", tone: "danger" }))) return;
 */
export function useConfirm() {
  const confirm = React.useContext(ConfirmContext);
  if (!confirm) throw new Error("useConfirm, ConfirmProvider içinde kullanılmalıdır.");
  return confirm;
}
export default ConfirmDialog;
